import axios from "axios";
import toast from "react-hot-toast";
import { getStore } from "../Utility/StoreRef/StoreRef";
import { setSessionExpired, updateAccessToken } from "../Redux/Slices/SessionSlice.js";
import { API_ENDPOINTS } from "./ApiEndPoints";
import { CONFIG, USE_MOCK_DATA } from "../Config/AppConfig";
import MockResponses from "./MockResponses.json";
import { logger } from "../utils/logger";
import { getApiErrorMessage } from "../utils/apiErrorMessage";
import { applyRememberMeStorageOnSessionEnd } from "../utils/rememberMeStorage";
import { saveAuthReturnUrl } from "../utils/authReturnUrl";
import { emitGlobalApiErrorModal } from "../utils/globalApiErrorModal";

const API_BASE_URL = CONFIG.API_BASE_URL;

const getToken = () => {
  const authToken = localStorage.getItem("authToken");
  if (!authToken) return "";

  try {
    const tokenData = JSON.parse(authToken);
    // Handle both camelCase (accessToken) and snake_case (access_token) formats
    const token = tokenData.accessToken || tokenData.access_token;
    return token ? `Bearer ${token}` : "";
  } catch {
    // Error parsing auth token - handled silently
    return "";
  }
};

const ApiBase = axios.create({
  baseURL: API_BASE_URL,
});

/** Axios/AbortController aborts (deduped GETs, navigation) — never surface as error toasts. */
function isRequestAbortError(err) {
  if (err == null) return false;
  if (err.canceled === true) return true;
  if (err.name === "CanceledError" || err.name === "AbortError") return true;
  if (err.code === "ERR_CANCELED" || err.code === "ECONNABORTED") return true;
  const m = typeof err.message === "string" ? err.message.trim().toLowerCase() : "";
  if (m === "canceled" || m === "cancelled") return true;
  return false;
}

const controllers = new Map();
const activeControllers = new Map();

// ✅ Add Authorization header (skip if caller already set one, e.g. setup-scoped token)
ApiBase.interceptors.request.use((config) => {
  if (!config.headers.Authorization) {
    config.headers.Authorization = getToken();
  }
  if (config.cancelKey) {
    if (activeControllers.has(config.cancelKey)) {
      activeControllers.get(config.cancelKey).abort();
    }
    const controller = new AbortController();
    config.signal = controller.signal;
    activeControllers.set(config.cancelKey, controller);
  }
  return config;
});

// ========================
// 🔁 TOKEN REFRESH HANDLING
// ========================
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

/** Build refresh URL (avoid double slash if base has trailing slash) */
const getRefreshUrl = () => {
  const base = (API_BASE_URL || "").replace(/\/$/, "");
  return `${base}/auth/refresh`;
};

/** Extract tokens from refresh API response. Supports:
 *  - { data: { accessToken, refreshToken } }
 *  - { accessToken, refreshToken }
 */
const getTokensFromRefreshResponse = (data) => {
  if (!data) return { accessToken: null, refreshToken: null };
  const payload = data.data ?? data;
  return {
    accessToken: payload.accessToken ?? payload.access_token ?? null,
    refreshToken: payload.refreshToken ?? payload.refresh_token ?? null,
  };
};

/** Only logout when refresh token is invalid/expired (401/403/498 from refresh endpoint) */
const shouldLogoutAfterRefreshFailure = (refreshErr) => {
  const status = refreshErr?.response?.status;
  return status === 401 || status === 403 || status === 498;
};

ApiBase.interceptors.response.use(
  (res) => {
    const method = res.config?.method?.toLowerCase();
    const skipToast = res.config?.skipToast === true;
    if (!skipToast && ["post", "put", "patch", "delete"].includes(method)) {
      const msg = res.data?.data?.message ?? res.data?.message;
      if (msg && typeof msg === "string") {
        toast.success(msg);
      }
    }
    return res;
  },
  async (err) => {
    const originalRequest = err.config;

    if (isRequestAbortError(err)) {
      return Promise.reject({ canceled: true });
    }

    // Skip token refresh for auth endpoints (login, refresh, logout)
    const isAuthEndpoint =
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/refresh") ||
      originalRequest.url?.includes("/auth/logout");

    // Handle both 401 (Unauthorized) and 498 (Token Expired) status codes
    if (
      (err.response?.status === 401 || err.response?.status === 498) &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;
      if (isRefreshing) {
        // Wait until refresh done
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              resolve(ApiBase(originalRequest));
            },
            reject: (err) => reject(err),
          });
        });
      }
      isRefreshing = true;
      try {
        const authToken = localStorage.getItem("authToken");
        if (!authToken) {
          throw new Error("No refresh token available");
        }

        const tokenData = JSON.parse(authToken);
        // Handle both camelCase (refreshToken) and snake_case (refresh_token) formats
        const refreshTokenValueRaw = tokenData.refreshToken ?? tokenData.refresh_token ?? "";
        const refreshTokenValue =
          typeof refreshTokenValueRaw === "string"
            ? refreshTokenValueRaw.trim()
            : refreshTokenValueRaw;

        // If refresh token is missing/empty, treat as session expired.
        if (!refreshTokenValue) {
          const store = getStore();
          localStorage.removeItem("authToken");
          store.dispatch(setSessionExpired(true));
          const e = new Error("Session expired");
          e._sessionExpired = true;
          throw e;
        }

        // POST /auth/refresh with body { refreshToken } – get new access token
        const { data: refreshData } = await axios.post(
          getRefreshUrl(),
          { refreshToken: refreshTokenValue },
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
          getTokensFromRefreshResponse(refreshData);

        if (!newAccessToken) {
          throw new Error("Invalid refresh response");
        }

        const updatedToken = {
          ...tokenData,
          accessToken: newAccessToken,
          access_token: newAccessToken,
          refreshToken: newRefreshToken ?? refreshTokenValue,
          refresh_token: newRefreshToken ?? refreshTokenValue,
        };
        localStorage.setItem("authToken", JSON.stringify(updatedToken));

        // Update Redux state
        const store = getStore();
        store.dispatch(updateAccessToken(newAccessToken));

        processQueue(null, newAccessToken);

        // Retry original request
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return ApiBase(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        const store = getStore();

        // Logout only when refresh token is expired/invalid (401/403/498 from refresh endpoint).
        // Do NOT logout on network error or other errors – user stays logged in.
        if (shouldLogoutAfterRefreshFailure(refreshErr)) {
          localStorage.removeItem("authToken");
          applyRememberMeStorageOnSessionEnd();
          store.dispatch(setSessionExpired(true));
          refreshErr._sessionExpired = true;
          if (window.location.pathname !== "/login") {
            saveAuthReturnUrl(window.location);
            window.location.href = "/login";
          }
        }
        // If we already marked session expired (e.g. missing/empty refresh token),
        // make sure we navigate to login and suppress toast.
        if (refreshErr?._sessionExpired) {
          if (window.location.pathname !== "/login") {
            saveAuthReturnUrl(window.location);
            window.location.href = "/login";
          }
        }

        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    // After retry: if we still get 401, refresh failed or token invalid – clear session once
    if (err.response?.status === 401 && err.config?._retry) {
      const store = getStore();
      localStorage.removeItem("authToken");
      applyRememberMeStorageOnSessionEnd();
      store.dispatch(setSessionExpired(true));
      err._sessionExpired = true;
      if (window.location.pathname !== "/login") {
        saveAuthReturnUrl(window.location);
        window.location.href = "/login";
      }
    }
    const requestMethod = String(originalRequest?.method || err.config?.method || "").toLowerCase();
    const isInlineErrorPreferredMethod = ["post", "put", "patch"].includes(requestMethod);
    const url = originalRequest?.url || err.config?.url || "";
    const isRefreshEndpoint = typeof url === "string" && url.includes("/auth/refresh");
    const isLogoutEndpoint = typeof url === "string" && url.includes("/auth/logout");

    const msg = getApiErrorMessage(err, "");

    const isRefreshTokenNoise =
      typeof msg === "string" &&
      (msg.toLowerCase().includes("refreshtoken should not be empty") ||
        msg.toLowerCase().includes("session revoked"));

    const isAbortLikeMessage =
      typeof msg === "string" &&
      (msg.trim() === "Request canceled" ||
        /^canceled$/i.test(msg.trim()) ||
        /^cancelled$/i.test(msg.trim()));

    const skipToast =
      err.config?.skipToast === true ||
      isInlineErrorPreferredMethod ||
      err._sessionExpired === true ||
      isRefreshEndpoint ||
      isLogoutEndpoint ||
      isRefreshTokenNoise ||
      isAbortLikeMessage;

    const shouldOpenGlobalErrorModal =
      isInlineErrorPreferredMethod &&
      !err.config?.suppressGlobalErrorModal &&
      !err._sessionExpired &&
      !isRefreshEndpoint &&
      !isLogoutEndpoint &&
      !isRefreshTokenNoise &&
      !isAbortLikeMessage &&
      msg &&
      typeof msg === "string";
    if (shouldOpenGlobalErrorModal) {
      err._globalErrorModalShown = true;
      emitGlobalApiErrorModal({ message: msg });
    }

    if (!skipToast && msg && typeof msg === "string") {
      toast.error(msg);
    }
    return Promise.reject(err);
  }
);

export const cancelRequest = (cancelKey) => {
  if (activeControllers.has(cancelKey)) {
    activeControllers.get(cancelKey).abort();
    activeControllers.delete(cancelKey);
  }
};

// ==============================
// 🚀 REQUEST CANCELLATION LOGIC
// ==============================

const cancelPreviousRequest = (key) => {
  if (controllers.has(key)) {
    const controller = controllers.get(key);
    controller.abort(); // cancel previous request
    controllers.delete(key);
  }
};

// 🔹 Helper to create unique request key
const createKey = (url, params = {}, data = {}) => {
  return `${url}?${JSON.stringify(params)}&${JSON.stringify(data)}`;
};

// 🔹 Helper to extract endpoint key from URL for mock data lookup
const getMockDataKey = (url) => {
  // Remove base URL and query params
  let endpoint = url.replace(API_BASE_URL, "").split("?")[0];

  // Remove leading/trailing slashes
  endpoint = endpoint.replace(/^\/+|\/+$/g, "");

  // Map common endpoint patterns to mock data keys
  const endpointMap = {
    clients: "clients",
    users: "users",
    issues: "issues",
    dashboard: "dashboard",
    "dashboard/summary-cards": "dashboard",
    "dashboard/recent-analyses": "dashboard",
    "dashboard/high-risk-portfolios": "dashboard",
    "dashboard/recently-updated-portfolios": "dashboard",
    "dashboard/activity-timeline": "dashboard",
    "time-entries/team-summary": "time-entries-team-summary",
    "time-entries/project-summary": "time-entries-project-summary",
    "billing/exports-preview": "billing-exports-preview",
    "qb/reports": "qb-reports",
    "qb/reports/export": "qb-reports-export",
    "billing/exports": "billing-exports",
    "billing/saas-module": "billing-saas-module",
    "auth/login": "auth/login",
    "auth/profile": "auth/profile",
    "auth/logout": "auth/logout",
    "auth/forgot-password": "auth/forgot-password",
    "audit-logs": "audit-logs",
    "month-locks": "month-locks",
  };

  // Check if we have a direct mapping (check full path first)
  if (endpointMap[endpoint]) {
    return endpointMap[endpoint];
  }

  // For auth endpoints, return the full path
  if (endpoint.startsWith("auth/")) {
    return endpoint;
  }

  // Extract the main resource (first part of path)
  const parts = endpoint.split("/");
  const mainResource = parts[0];

  // Return the main resource as the key (e.g., "clients", "dashboard")
  return mainResource || endpoint;
};

// ========================
// 🌐 API WRAPPER FUNCTIONS, TEST
// ========================

/**
 * @param {string} url
 * @param {Record<string, unknown>} [params]
 * @param {Record<string, string>} [headers]
 * @param {{ requestKey?: string }} [options] — when `requestKey` is set, dedupe/cancel uses it instead of `url`+`params` (e.g. one slot for GET /work-items vs GET /work-items/my).
 */
export const get = async (url, params = {}, headers = {}, options = {}) => {
  const key = options.requestKey ?? createKey(url, params);
  cancelPreviousRequest(key);
  const controller = new AbortController();
  controllers.set(key, controller);

  if (USE_MOCK_DATA) {
    // Get mock data key from URL
    const mockDataKey = getMockDataKey(url);
    const mockData = MockResponses[mockDataKey];

    logger.log("Mock response for:", mockDataKey, mockData);

    // GET /month-locks — list (POST still uses `result` for create)
    if (mockDataKey === "month-locks" && mockData?.useMockResponse && mockData.listResult) {
      controllers.delete(key);
      return { data: mockData.listResult };
    }

    if (mockData && mockData.useMockResponse) {
      controllers.delete(key);
      return { data: mockData.result };
    }
  }

  try {
    const response = await ApiBase.get(url, {
      params,
      headers,
      signal: controller.signal,
    });
    logger.log("Response for:", url, response);
    controllers.delete(key);
    return response;
  } catch (err) {
    controllers.delete(key);
    const aborted =
      err?.name === "CanceledError" ||
      err?.name === "AbortError" ||
      err?.code === "ERR_CANCELED" ||
      err?.canceled === true;
    if (aborted) {
      return Promise.reject("Request canceled");
    }
    throw err;
  }
};

/** GET returning binary (CSV/XLSX, etc.) — same auth as {@link get}. */
export const getBlob = async (url, params = {}, headers = {}) => {
  const key = `${createKey(url, params)}:blob`;
  cancelPreviousRequest(key);
  const controller = new AbortController();
  controllers.set(key, controller);

  try {
    const response = await ApiBase.get(url, {
      params,
      headers,
      responseType: "blob",
      signal: controller.signal,
    });
    controllers.delete(key);
    return response;
  } catch (err) {
    controllers.delete(key);
    const aborted =
      err?.name === "CanceledError" ||
      err?.name === "AbortError" ||
      err?.code === "ERR_CANCELED" ||
      err?.canceled === true;
    if (aborted) {
      return Promise.reject("Request canceled");
    }
    throw err;
  }
};

export const post = async (url, data, headers = {}, responseType = "json", axiosConfig = {}) => {
  const key = createKey(url, {}, data);
  cancelPreviousRequest(key);
  const controller = new AbortController();
  controllers.set(key, controller);

  if (USE_MOCK_DATA) {
    // Get mock data key from URL
    const mockDataKey = getMockDataKey(url);
    const mockData = MockResponses[mockDataKey];

    logger.log("Mock response for:", mockDataKey, mockData);

    // POST /billing/exports — distinct from GET history mock (`billing-exports` list)
    if (mockDataKey === "billing-exports") {
      const createMock = MockResponses["billing-exports-create"];
      if (createMock?.useMockResponse) {
        controllers.delete(key);
        await new Promise((resolve) => setTimeout(resolve, 400));
        return { data: createMock.result };
      }
    }

    // Special handling for login endpoint - validate credentials
    if (mockDataKey === "auth/login" && mockData && mockData.useMockResponse) {
      controllers.delete(key);
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Validate credentials
      const email = data?.Email || data?.email || "";
      const password = data?.Password || data?.password || "";

      if (email === "info@itptek.com" && password === "itp@2026") {
        return { data: mockData.result };
      } else {
        // Return error for invalid credentials
        const error = new Error("Invalid credentials");
        error.response = {
          data: {
            message: "Invalid email or password. Please try again.",
            errors: {},
          },
        };
        throw error;
      }
    }

    // Exclude billing-exports here: GET uses `billing-exports` list; POST uses `billing-exports-create` above
    if (mockData && mockData.useMockResponse && mockDataKey !== "billing-exports") {
      controllers.delete(key);
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500));
      return { data: mockData.result };
    }
  }

  try {
    const response = await ApiBase.post(url, data, {
      headers,
      responseType,
      signal: controller.signal,
      ...axiosConfig,
    });
    controllers.delete(key);
    return response;
  } catch (err) {
    controllers.delete(key);
    if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
      return Promise.reject("Request canceled");
    }
    throw err;
  }
};

/**
 * POST multipart/form-data (file upload). Do not set Content-Type so browser sends boundary.
 * Body format: Content-Disposition: form-data; name="files"; filename="sample.txt"
 */
export const postMultipart = async (url, formData, headers = {}, axiosConfig = {}) => {
  const key = createKey(url, {}, "multipart");
  cancelPreviousRequest(key);
  const controller = new AbortController();
  controllers.set(key, controller);
  try {
    const response = await ApiBase.post(url, formData, {
      headers: { ...headers, "Content-Type": undefined },
      transformRequest: [
        (data, reqHeaders) => {
          if (data instanceof FormData) {
            delete reqHeaders["Content-Type"];
            return data;
          }
          return data;
        },
      ],
      signal: controller.signal,
      ...axiosConfig,
    });
    controllers.delete(key);
    return response;
  } catch (err) {
    controllers.delete(key);
    if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
      return Promise.reject("Request canceled");
    }
    throw err;
  }
};

/**
 * PUT multipart/form-data (e.g. project create/update with documents). Do not set Content-Type so browser sends boundary.
 */
export const putMultipart = async (url, formData, headers = {}, axiosConfig = {}) => {
  const key = createKey(url, {}, "multipart-put");
  cancelPreviousRequest(key);
  const controller = new AbortController();
  controllers.set(key, controller);
  try {
    const response = await ApiBase.put(url, formData, {
      headers: { ...headers, "Content-Type": undefined },
      transformRequest: [
        (data, reqHeaders) => {
          if (data instanceof FormData) {
            delete reqHeaders["Content-Type"];
            return data;
          }
          return data;
        },
      ],
      signal: controller.signal,
      ...axiosConfig,
    });
    controllers.delete(key);
    return response;
  } catch (err) {
    controllers.delete(key);
    if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
      return Promise.reject("Request canceled");
    }
    throw err;
  }
};

/**
 * PATCH multipart/form-data (work item update with attachments). Do not set Content-Type so browser sends boundary.
 */
export const patchMultipart = async (url, formData, headers = {}, axiosConfig = {}) => {
  const key = createKey(url, {}, "multipart-patch");
  cancelPreviousRequest(key);
  const controller = new AbortController();
  controllers.set(key, controller);
  try {
    const response = await ApiBase.patch(url, formData, {
      headers: { ...headers, "Content-Type": undefined },
      transformRequest: [
        (data, reqHeaders) => {
          if (data instanceof FormData) {
            delete reqHeaders["Content-Type"];
            return data;
          }
          return data;
        },
      ],
      signal: controller.signal,
      ...axiosConfig,
    });
    controllers.delete(key);
    return response;
  } catch (err) {
    controllers.delete(key);
    if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
      return Promise.reject("Request canceled");
    }
    throw err;
  }
};

export const put = async (url, data, headers = {}) => {
  const key = createKey(url, {}, data);
  cancelPreviousRequest(key);
  const controller = new AbortController();
  controllers.set(key, controller);

  try {
    const response = await ApiBase.put(url, data, {
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      signal: controller.signal,
    });
    controllers.delete(key);
    return response;
  } catch (err) {
    controllers.delete(key);
    if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
      return Promise.reject("Request canceled");
    }
    throw err;
  }
};

export const del = async (url, params = {}, config = {}) => {
  const key = createKey(url, params);
  cancelPreviousRequest(key);
  const controller = new AbortController();
  controllers.set(key, controller);

  try {
    const response = await ApiBase.delete(url, {
      params,
      signal: controller.signal,
      ...config,
    });
    controllers.delete(key);
    return response;
  } catch (err) {
    controllers.delete(key);
    if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
      return Promise.reject("Request canceled");
    }
    throw err;
  }
};

export const patch = async (url, data = {}, query = {}, headers = {}, axiosConfig = {}) => {
  const key = createKey(url, query, data);
  cancelPreviousRequest(key);
  const controller = new AbortController();
  controllers.set(key, controller);

  try {
    const response = await ApiBase.patch(url, data, {
      params: query,
      headers,
      signal: controller.signal,
      ...axiosConfig,
    });
    controllers.delete(key);
    return response;
  } catch (err) {
    controllers.delete(key);
    if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
      return Promise.reject("Request canceled");
    }
    throw err;
  }
};

// Export API_ENDPOINTS for convenience
export { API_ENDPOINTS };

// Exported only for testing — attach axios-mock-adapter to this instance in tests.
// Do NOT use this directly in production code; use the exported wrapper functions instead.
export { ApiBase as _instance };
